import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Box,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useApi } from "../../../utils/Api";
import Translate from "../../../utils/Translate";
import HtmlEditor from "../../HtmlEditor";
import { TabPanel } from "../../settings/TabPanel";
import Preloader from "../../Preloader";

const MAIN_URL = import.meta.env.VITE_MAIN_URL;

const ID_PATTERN = /^[a-z_]*$/;

const emptyForm = {
  titleSwe: "",
  titleEng: "",
  titleFra: "",
  contentHtmlSwe: "",
  contentHtmlEng: "",
  contentHtmlFra: "",
  categoryIds: [],
  iconName: "",
  description: "",
};

function categoryLabel(category) {
  const name = category.description || category.reference || `#${category.helpCategoryId}`;
  return `${name} (${category.accessKey})`;
}

export default function HelpArticleEditDialog({ helpPageId, existingIds, categories, onSaved }) {
  const api = useApi();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [currentId, setCurrentId] = useState(helpPageId ?? "");
  const [newId, setNewId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [langTab, setLangTab] = useState(0);
  const [loadingContent, setLoadingContent] = useState(!!helpPageId);
  const [submitting, setSubmitting] = useState(false);

  const categoryList = categories ?? [];

  useEffect(() => {
    if (!currentId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingContent(true);
      setLangTab(0);
      const response = await api.fetch(`${MAIN_URL}help/admin/pages/${encodeURIComponent(currentId)}`, false, "GET");
      if (cancelled) {
        return;
      }
      if (response && response.isSuccessful) {
        setForm({
          titleSwe: response.titleSwe ?? "",
          titleEng: response.titleEng ?? "",
          titleFra: response.titleFra ?? "",
          contentHtmlSwe: response.contentHtmlSwe ?? "",
          contentHtmlEng: response.contentHtmlEng ?? "",
          contentHtmlFra: response.contentHtmlFra ?? "",
          categoryIds: response.categoryIds ?? [],
          iconName: response.iconName ?? "",
          description: response.description ?? "",
        });
      } else {
        setForm(emptyForm);
      }
      setLoadingContent(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [currentId, api]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  const selectedCategories = categoryList.filter((c) => (form.categoryIds ?? []).includes(c.helpCategoryId));

  const idIsUnique = !(existingIds ?? []).includes(newId);
  const idIsValid = !!newId && ID_PATTERN.test(newId) && newId.length <= 20 && idIsUnique;
  const hasAnyTitleOrContent =
    !!form.titleSwe ||
    !!form.titleEng ||
    !!form.titleFra ||
    !!form.contentHtmlSwe ||
    !!form.contentHtmlEng ||
    !!form.contentHtmlFra;

  const canCreate =
    idIsValid && !!form.description && (!!form.titleSwe || !!form.titleEng || !!form.titleFra) && !submitting;
  const canSave =
    !loadingContent && !!form.description && (form.categoryIds ?? []).length > 0 && hasAnyTitleOrContent && !submitting;

  async function handleCreate() {
    setSubmitting(true);
    const response = await api.fetch(
      `${MAIN_URL}help/admin/pages`,
      {
        helpPageId: newId,
        description: form.description,
        titleSwe: form.titleSwe,
        titleEng: form.titleEng,
        titleFra: form.titleFra,
      },
      "POST",
    );
    setSubmitting(false);
    if (response && response.isSuccessful) {
      enqueueSnackbar(Translate.get("HelpArticleCreated"), { variant: "success" });
      if (onSaved) {
        onSaved();
      }
      setCurrentId(newId);
    }
  }

  async function handleSave() {
    setSubmitting(true);
    const response = await api.fetch(
      `${MAIN_URL}help/admin/pages/${encodeURIComponent(currentId)}`,
      {
        titleSwe: form.titleSwe,
        titleEng: form.titleEng,
        titleFra: form.titleFra,
        contentHtmlSwe: form.contentHtmlSwe,
        contentHtmlEng: form.contentHtmlEng,
        contentHtmlFra: form.contentHtmlFra,
        categoryIds: form.categoryIds,
        iconName: form.iconName,
        description: form.description,
      },
      "PUT",
    );
    setSubmitting(false);
    if (response && response.isSuccessful) {
      enqueueSnackbar(Translate.get("HelpArticleSaved"), { variant: "success" });
      if (onSaved) {
        onSaved();
      }
      navigate(-1);
    }
  }

  const isCreateMode = !currentId;

  if (loadingContent) {
    return <Preloader />;
  }

  return (
    <>
      <DialogContent>
        <Box sx={{ minWidth: 720, pt: 2 }}>
          <Stack spacing={3}>
            {isCreateMode && (
              <TextField
                label={Translate.get("HelpArticleId")}
                helperText={
                  !newId || ID_PATTERN.test(newId)
                    ? idIsUnique
                      ? Translate.get("HelpArticleIdHint")
                      : Translate.get("HelpArticleIdNotUnique")
                    : Translate.get("HelpArticleIdInvalid")
                }
                error={(!!newId && !ID_PATTERN.test(newId)) || !idIsUnique}
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
                inputProps={{ maxLength: 20 }}
                fullWidth
                required
              />
            )}
            {!isCreateMode && (
              <TextField label={Translate.get("HelpArticleId")} value={currentId} fullWidth required disabled />
            )}
            <TextField
              label={Translate.get("HelpArticleDescriptionInternal")}
              helperText={Translate.get("HelpArticleDescriptionHint")}
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              fullWidth
              required
            />
            {!isCreateMode && (
              <>
                <Autocomplete
                  multiple
                  options={categoryList}
                  value={selectedCategories}
                  getOptionLabel={categoryLabel}
                  isOptionEqualToValue={(option, value) => option.helpCategoryId === value.helpCategoryId}
                  onChange={(e, value) =>
                    updateField(
                      "categoryIds",
                      value.map((c) => c.helpCategoryId),
                    )
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={Translate.get("HelpArticleCategories")} required />
                  )}
                />
                <TextField
                  label={Translate.get("HelpArticleIconName")}
                  value={form.iconName}
                  onChange={(e) => updateField("iconName", e.target.value)}
                  fullWidth
                />
              </>
            )}
          </Stack>

          <Tabs value={langTab} onChange={(e, v) => setLangTab(v)} sx={{ mt: 3 }}>
            <Tab label={Translate.get("LanguageCodeEnum_Swedish")} />
            <Tab label={Translate.get("LanguageCodeEnum_English")} />
            <Tab label={Translate.get("LanguageCodeEnum_French")} />
          </Tabs>

          <TabPanel value={langTab} index={0}>
            <Stack spacing={2}>
              <TextField
                label={`${Translate.get("TitleLabel")} (${Translate.get("LanguageCodeEnum_Swedish")})`}
                value={form.titleSwe}
                onChange={(e) => updateField("titleSwe", e.target.value)}
                fullWidth
              />
              {!isCreateMode && (
                <HtmlEditor
                  label={`${Translate.get("HelpArticleContent")} (${Translate.get("LanguageCodeEnum_Swedish")})`}
                  htmlContent={form.contentHtmlSwe}
                  allowImages
                  allowLinks
                  onBlur={(content) => updateField("contentHtmlSwe", content)}
                />
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={langTab} index={1}>
            <Stack spacing={2}>
              <TextField
                label={`${Translate.get("TitleLabel")} (${Translate.get("LanguageCodeEnum_English")})`}
                value={form.titleEng}
                onChange={(e) => updateField("titleEng", e.target.value)}
                fullWidth
              />
              {!isCreateMode && (
                <HtmlEditor
                  label={`${Translate.get("HelpArticleContent")} (${Translate.get("LanguageCodeEnum_English")})`}
                  htmlContent={form.contentHtmlEng}
                  allowImages
                  allowLinks
                  onBlur={(content) => updateField("contentHtmlEng", content)}
                />
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={langTab} index={2}>
            <Stack spacing={2}>
              <TextField
                label={`${Translate.get("TitleLabel")} (${Translate.get("LanguageCodeEnum_French")})`}
                value={form.titleFra}
                onChange={(e) => updateField("titleFra", e.target.value)}
                fullWidth
              />
              {!isCreateMode && (
                <HtmlEditor
                  label={`${Translate.get("HelpArticleContent")} (${Translate.get("LanguageCodeEnum_French")})`}
                  htmlContent={form.contentHtmlFra}
                  allowImages
                  allowLinks
                  onBlur={(content) => updateField("contentHtmlFra", content)}
                />
              )}
            </Stack>
          </TabPanel>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigate(-1)}>{Translate.get("Cancel")}</Button>
        {isCreateMode ? (
          <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
            {Translate.get("Save")}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSave} disabled={!canSave}>
            {Translate.get("Save")}
          </Button>
        )}
      </DialogActions>
    </>
  );
}
